import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// M3X.COMP F.17 — annual merit cycle list + new.
export function CompCyclesPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    effective_date: new Date().toISOString().slice(0, 10),
    budget_envelope_pct: "5",
    cost_of_living_pct: "2",
  });

  const q = useQuery({
    queryKey: ["comp-cycles", legalEntityId],
    queryFn: () => api.listCompCycles(legalEntityId!),
    enabled: !!legalEntityId,
  });

  const create = useMutation({
    mutationFn: () =>
      api.createCompCycle({
        legal_entity_id: legalEntityId,
        code: form.code,
        name_en: form.name_en,
        name_ar: form.name_ar,
        effective_date: form.effective_date,
        budget_envelope_pct: form.budget_envelope_pct,
        cost_of_living_pct: form.cost_of_living_pct,
      }),
    onSuccess: () => {
      setCreating(false);
      queryClient.invalidateQueries({
        queryKey: ["comp-cycles", legalEntityId],
      });
    },
  });

  if (gate) return gate;
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel message={(q.error as Error).message} />;

  const cycles = q.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("comp_cycles.title")}
        subtitle={t("comp_cycles.subtitle")}
        actions={
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-1 rounded bg-brand-deep px-3 py-1.5 text-sm text-white hover:bg-slate-900"
          >
            <Plus className="h-4 w-4" />
            {t("comp_cycles.new")}
          </button>
        }
      />

      {creating && (
        <div className="space-y-3 rounded border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-3 gap-3">
            <input
              placeholder={t("comp_cycles.code")}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              placeholder={t("comp_cycles.name_en")}
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              placeholder={t("comp_cycles.name_ar")}
              value={form.name_ar}
              onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex flex-col text-xs">
              <span>{t("comp_cycles.effective_date")}</span>
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) =>
                  setForm({ ...form, effective_date: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span>{t("comp_cycles.budget_envelope_pct")}</span>
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={form.budget_envelope_pct}
                onChange={(e) =>
                  setForm({ ...form, budget_envelope_pct: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span>{t("comp_cycles.cost_of_living_pct")}</span>
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={form.cost_of_living_pct}
                onChange={(e) =>
                  setForm({ ...form, cost_of_living_pct: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
          </div>
          <button
            type="button"
            disabled={!form.code || !form.name_en || create.isPending}
            onClick={() => create.mutate()}
            className="rounded bg-brand-deep px-3 py-1.5 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
          >
            {t("comp_cycles.create")}
          </button>
        </div>
      )}

      {cycles.length === 0 ? (
        <EmptyState
          icon={Banknote}
          headline={t("comp_cycles.empty_title")}
          description={t("comp_cycles.empty_desc")}
        />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th>{t("comp_cycles.code")}</th>
              <th>{t("comp_cycles.effective_date")}</th>
              <th>{t("comp_cycles.budget_envelope_pct")}</th>
              <th>{t("comp_cycles.col_state")}</th>
            </tr>
          </thead>
          <tbody>
            {cycles.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2">
                  <Link
                    to={`/operations/comp-cycle/${c.id}`}
                    className="text-brand-deep hover:underline"
                  >
                    {c.code}
                  </Link>
                </td>
                <td>{c.effective_date}</td>
                <td>{c.budget_envelope_pct}%</td>
                <td>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                    {c.workflow_state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
